/**
 * The windows a presentation draws outside React Flow: one per focused frame
 * in its box; review and deliverables draw their active frame alone.
 */
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import type { PresentationState } from "@/lib/focus/focus-types";

export interface PresentedWindow {
  frameId: string;
  box: FocusWindowBox;
  active: boolean;
}

/** The box frame `frameId` is drawn in, if the presentation draws it. */
export function presentedWindowBox(
  presentation: PresentationState | null,
  frameId: string,
): FocusWindowBox | null {
  if (!presentation) return null;
  const index = presentation.frameIds.indexOf(frameId);
  if (index < 0) return null;
  if (presentation.variant === "focus")
    return presentation.boxes[index] ?? null;
  return index === presentation.activeIndex
    ? (presentation.boxes[0] ?? null)
    : null;
}

/**
 * Every drawn window in tick order. The active one stacks on top by z-index
 * rather than DOM order: moving a window in the DOM reloads its iframes.
 */
export function presentedWindows(
  presentation: PresentationState,
  shownIds?: ReadonlySet<string>,
): PresentedWindow[] {
  const activeId = presentation.frameIds[presentation.activeIndex];
  return presentation.frameIds.flatMap((frameId) => {
    if (shownIds && !shownIds.has(frameId)) return [];
    const box = presentedWindowBox(presentation, frameId);
    return box ? [{ frameId, box, active: frameId === activeId }] : [];
  });
}
